import React, { useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { prefixClass } from 'lib/utils';
import { IconFolder } from 'components/Icons';
import KSInputField from 'presentation/components/KSInputField';
import { KSButton } from '../../Common';
import { MobileSlideUpPanel } from '../MobileSlideUpPanel/MobileSlideUpPanel';
import { useCreateGroupMutation } from 'hooks/queue/useGroupOperationsMutation';
import './MobileCreateGroupPanel.scss';

interface MobileCreateGroupPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export const MobileCreateGroupPanel: React.FC<MobileCreateGroupPanelProps> = ({ isOpen, onClose }) => {
  const prefix = prefixClass('mobile-create-group-panel');
  const { locationId } = useParams();
  const [title, setTitle] = useState('');

  const { mutate: createGroup, isPending } = useCreateGroupMutation(locationId || '');

  const handleSubmit = useCallback(() => {
    if (!title.trim() || isPending) return;
    createGroup(
      { locationId: locationId || '', title: title.trim() },
      {
        onSuccess: () => {
          setTitle('');
          onClose();
        },
      }
    );
  }, [title, isPending, createGroup, locationId, onClose]);

  const handleClose = useCallback(() => {
    setTitle('');
    onClose();
  }, [onClose]);

  return (
    <MobileSlideUpPanel
      isOpen={isOpen}
      onClose={handleClose}
      title="New Group"
      titleIcon={<IconFolder />}
      useFitContent
      resizable={false}
    >
      <div className={prefix()}>
        <div className={prefix('__form')}>
          <div className={prefix('__row')}>
            <label className={prefix('__label')}>Title</label>
            <KSInputField
              value={title}
              placeholder="New group title"
              onChange={e => setTitle(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
              disabled={isPending}
            />
          </div>
        </div>
        <div className={prefix('__actions')}>
          <KSButton variant="secondary" label="Cancel" onClick={handleClose} disabled={isPending} />
          <KSButton
            variant="primary"
            label={isPending ? 'Creating...' : 'Create'}
            onClick={handleSubmit}
            disabled={isPending || !title.trim()}
          />
        </div>
      </div>
    </MobileSlideUpPanel>
  );
};

MobileCreateGroupPanel.displayName = 'MobileCreateGroupPanel';

export default MobileCreateGroupPanel;
